import React, { useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, Divider,
} from '@mui/material';
import SoftBox from 'components/SoftBox';
import SoftTypography from 'components/SoftTypography';
import SoftButton from 'components/SoftButton';
import SoftInput from 'components/SoftInput';

import { importSalesLeads } from 'services/salesLeadService';
import { IMPORTABLE_FIELDS } from '../fields';

const NONE = '';
const REQUIRED = ['contactName', 'businessName'];
const DEFAULT_FIELDS = [
  { name: 'salesRep', label: 'Sales Rep', type: 'text' },
  { name: 'salesRepPhone', label: 'Sales Rep Phone', type: 'text' },
  { name: 'trialDays', label: 'Trial Period (days)', type: 'number' },
  { name: 'trialScans', label: 'Trial Scans', type: 'number' },
  { name: 'discountPercent', label: 'Discount (%)', type: 'number' },
];

const EMPTY_DEFAULTS = DEFAULT_FIELDS.reduce((acc, f) => ({ ...acc, [f.name]: '' }), {});

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');

// Guess which spreadsheet column belongs to each field by fuzzy header match.
const guessMapping = (headers) => {
  const mapping = {};
  IMPORTABLE_FIELDS.forEach((f) => {
    const target = norm(f.label);
    const targetName = norm(f.name);
    const idx = headers.findIndex((h) => {
      const nh = norm(h);
      return nh === targetName || nh === target || (nh && (nh.includes(targetName) || targetName.includes(nh)));
    });
    mapping[f.name] = idx >= 0 ? String(idx) : NONE;
  });
  return mapping;
};

const selectStyle = {
  width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d2d6da', fontSize: '14px',
};

function ImportLeadsDialog({ open, onClose, onImported }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | configure | importing | result
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState(EMPTY_DEFAULTS);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const reset = () => {
    setStep('upload'); setFileName(''); setHeaders([]); setRows([]);
    setMapping({}); setDefaults(EMPTY_DEFAULTS);
    setError(''); setResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleClose = () => { reset(); onClose(); };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false });
        if (!data.length) { setError('The spreadsheet appears to be empty.'); return; }
        const hdrs = (data[0] || []).map((h) => String(h ?? '').trim());
        const dataRows = data.slice(1).filter((r) => r.some((c) => String(c ?? '').trim() !== ''));
        if (!dataRows.length) { setError('No data rows found below the header row.'); return; }
        setHeaders(hdrs);
        setRows(dataRows);
        setMapping(guessMapping(hdrs));
        setStep('configure');
      } catch (err) {
        console.error('Error parsing spreadsheet:', err);
        setError('Could not read this file. Please upload a valid .xlsx, .xls or .csv file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Build the lead objects from the current mapping (skip empty values).
  const buildLeads = () => rows.map((row) => {
    const lead = {};
    Object.entries(mapping).forEach(([field, colIdx]) => {
      if (colIdx === NONE) return;
      const value = String(row[Number(colIdx)] ?? '').trim();
      if (value !== '') lead[field] = value;
    });
    return lead;
  });

  const mappedRequiredMissing = REQUIRED.filter((f) => mapping[f] === NONE || mapping[f] === undefined);
  const previewLeads = step === 'configure' ? buildLeads().slice(0, 5) : [];

  const doImport = async () => {
    setStep('importing');
    setError('');
    try {
      const leads = buildLeads();
      const cleanDefaults = Object.fromEntries(
        Object.entries(defaults).filter(([, v]) => v !== '' && v !== null)
      );
      const summary = await importSalesLeads({ leads, defaults: cleanDefaults });
      setResult(summary);
      setStep('result');
      onImported?.();
    } catch (err) {
      console.error('Error importing leads:', err);
      setError('Import failed. Please try again.');
      setStep('configure');
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Sales Leads</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {step === 'upload' && (
          <SoftBox py={2}>
            <SoftTypography variant="body2" color="text" mb={2}>
              Upload an Excel (.xlsx, .xls) or CSV file. The first row must contain column headers.
              Every imported lead is created as a <strong>Dealer</strong> lead.
            </SoftTypography>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={onFile}
            />
          </SoftBox>
        )}

        {step === 'configure' && (
          <SoftBox>
            <SoftTypography variant="button" fontWeight="bold">
              {fileName} — {rows.length} row{rows.length === 1 ? '' : 's'}
            </SoftTypography>

            <SoftTypography variant="h6" mt={2} mb={1}>Map Columns</SoftTypography>
            <SoftTypography variant="caption" color="text">
              Match each lead attribute to a column in your spreadsheet. Contact Name and
              Dealership / Business Name are required — rows missing either are skipped.
            </SoftTypography>
            <Grid container spacing={2} mt={0.5}>
              {IMPORTABLE_FIELDS.map((f) => (
                <Grid item xs={12} sm={6} key={f.name}>
                  <SoftTypography variant="caption" fontWeight="bold">
                    {f.label}{REQUIRED.includes(f.name) ? ' *' : ''}
                  </SoftTypography>
                  <select
                    style={selectStyle}
                    value={mapping[f.name] ?? NONE}
                    onChange={(e) => setMapping((m) => ({ ...m, [f.name]: e.target.value }))}
                  >
                    <option value={NONE}>— Don&apos;t import —</option>
                    {headers.map((h, i) => (
                      <option key={i} value={String(i)}>{h || `Column ${i + 1}`}</option>
                    ))}
                  </select>
                </Grid>
              ))}
            </Grid>

            {mappedRequiredMissing.length > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Map Contact Name and Dealership / Business Name to import any leads.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <SoftTypography variant="h6" mb={1}>Default Terms</SoftTypography>
            <SoftTypography variant="caption" color="text">
              These apply to every lead in this import.
            </SoftTypography>
            <Grid container spacing={2} mt={0.5}>
              {DEFAULT_FIELDS.map((f) => (
                <Grid item xs={12} sm={4} key={f.name}>
                  <SoftTypography variant="caption" fontWeight="bold">{f.label}</SoftTypography>
                  <SoftInput
                    type={f.type}
                    placeholder={f.label}
                    value={defaults[f.name]}
                    onChange={(e) => setDefaults((d) => ({ ...d, [f.name]: e.target.value }))}
                  />
                </Grid>
              ))}
            </Grid>

            {previewLeads.length > 0 && (
              <>
                <SoftTypography variant="h6" mt={3} mb={1}>Preview (first {previewLeads.length})</SoftTypography>
                <TableContainer>
                  <Table size="small">
                    <TableHead sx={{ display: 'table-header-group' }}>
                      <TableRow>
                        <TableCell>Contact</TableCell>
                        <TableCell>Business</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {previewLeads.map((l, i) => (
                        <TableRow key={i}>
                          <TableCell>{l.contactName || '—'}</TableCell>
                          <TableCell>{l.businessName || '—'}</TableCell>
                          <TableCell>{l.businessEmail || '—'}</TableCell>
                          <TableCell>{l.businessPhone || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </SoftBox>
        )}

        {step === 'importing' && (
          <SoftBox display="flex" alignItems="center" gap={2} py={3}>
            <CircularProgress size={24} />
            <SoftTypography variant="body2">Importing leads…</SoftTypography>
          </SoftBox>
        )}

        {step === 'result' && result && (
          <SoftBox py={2}>
            <Alert severity="success" sx={{ mb: 2 }}>
              Created {result.created} lead{result.created === 1 ? '' : 's'}.
              {result.skipped?.length ? ` Skipped ${result.skipped.length}.` : ''}
            </Alert>
            {result.skipped?.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ display: 'table-header-group' }}>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>Reason skipped</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.skipped.map((s, i) => (
                      <TableRow key={i}>
                        <TableCell>{s.row + 2}{/* +2: 1-based + header row */}</TableCell>
                        <TableCell>{s.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </SoftBox>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'configure' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <SoftButton
              variant="gradient"
              color="info"
              disabled={mappedRequiredMissing.length > 0}
              onClick={doImport}
            >
              Import {rows.length} row{rows.length === 1 ? '' : 's'}
            </SoftButton>
          </>
        )}
        {step === 'upload' && <Button onClick={handleClose}>Cancel</Button>}
        {step === 'result' && (
          <SoftButton variant="gradient" color="info" onClick={handleClose}>Done</SoftButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImportLeadsDialog;
